import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Award, Share2, CheckCircle, ExternalLink } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CertificatePage = () => {
  const { certificateId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [certificate, setCertificate] = useState(null);
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);

  const fetchCertificate = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/exams/certificates/${certificateId}`);
      setCertificate(response.data);
    } catch (err) {
      setError('Certificado não encontrado');
    } finally {
      setLoading(false);
    }
  }, [certificateId]);

  useEffect(() => {
    fetchCertificate();
  }, [fetchCertificate]);

  const handleDownloadCertificate = async () => {
    setDownloading(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/exams/certificates/${certificateId}/download`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `certificado_${certificate.certificate_code}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Erro ao baixar certificado:', err);
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadBadge = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/exams/certificates/${certificateId}/badge`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `badge_${certificate.certificate_code}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Erro ao baixar badge:', err);
    }
  };

  const handleShare = () => {
    const url = `${window.location.origin}/validar/${certificate.certificate_code}`;
    if (navigator.share) {
      navigator.share({
        title: `Certificado AJ BIM Academy - ${certificate.course_title}`,
        text: `${certificate.user_name} concluiu o curso ${certificate.course_title} na AJ BIM Academy!`,
        url: url
      });
    } else {
      navigator.clipboard.writeText(url);
      alert('Link copiado para a área de transferência!');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-red-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white">Carregando certificado...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black pt-24 pb-12">
        <div className="container mx-auto px-4 max-w-2xl text-center">
          <Award className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-4">Certificado não encontrado</h1>
          <p className="text-gray-400 mb-6">{error}</p>
          <Button onClick={() => navigate('/')} className="bg-red-600 hover:bg-red-700">
            Voltar ao Início
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black pt-24 pb-12">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Header */}
        <div className="mb-8">
          <Button
            onClick={() => navigate(-1)}
            variant="ghost"
            className="text-white hover:text-red-500 mb-4"
          >
            <ArrowLeft className="mr-2" size={20} />
            Voltar
          </Button>
        </div>

        {/* Certificate Preview */}
        <Card className="bg-gradient-to-br from-zinc-900 via-red-950/30 to-zinc-900 border-2 border-red-800 mb-8 overflow-hidden">
          {/* Decorative corners */}
          <div className="absolute top-4 left-4 w-16 h-16 border-l-2 border-t-2 border-yellow-500/50"></div>
          <div className="absolute top-4 right-4 w-16 h-16 border-r-2 border-t-2 border-yellow-500/50"></div>
          <div className="absolute bottom-4 left-4 w-16 h-16 border-l-2 border-b-2 border-yellow-500/50"></div>
          <div className="absolute bottom-4 right-4 w-16 h-16 border-r-2 border-b-2 border-yellow-500/50"></div>
          
          <CardContent className="relative pt-12 pb-12 text-center">
            {/* Logo/Header */}
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-white mb-2">AJ | BIM ACADEMY</h2>
              <div className="inline-block px-6 py-1 border-t border-b border-yellow-500/50">
                <span className="text-yellow-500 text-sm tracking-widest">CERTIFICADO DE CONCLUSÃO</span>
              </div>
            </div>

            {/* Main Content */}
            <div className="mb-8">
              <p className="text-gray-400 mb-4">Certificamos que</p>
              <h1 className="text-4xl font-bold text-white mb-2">{certificate.user_name.toUpperCase()}</h1>
              <div className="w-64 h-0.5 bg-yellow-500/50 mx-auto mb-6"></div>
              
              <p className="text-gray-400 mb-4">concluiu com êxito o curso</p>
              <h2 className="text-2xl font-bold text-yellow-500 mb-4">{certificate.course_title}</h2>
              
              <div className="flex justify-center gap-8 text-sm text-gray-400">
                <span>Carga Horária: <strong className="text-white">{certificate.course_hours}h</strong></span>
                <span>Nota Final: <strong className="text-white">{certificate.score.toFixed(1)}</strong></span>
                <span>Modalidade: <strong className="text-white">EaD</strong></span>
              </div>
            </div>

            {/* Approved Badge */}
            <div className="inline-flex items-center gap-2 px-6 py-2 bg-green-600/20 border border-green-600 rounded-full mb-8">
              <CheckCircle className="text-green-500" size={20} />
              <span className="text-green-400 font-semibold">APROVADO</span>
            </div>

            {/* Footer */}
            <div className="border-t border-zinc-700 pt-6">
              <p className="text-gray-500 text-sm mb-2">
                Emitido em {formatDate(certificate.issued_at)}
              </p>
              <p className="text-yellow-500 font-mono text-sm">
                Código: {certificate.certificate_code}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Button
            onClick={handleDownloadCertificate}
            disabled={downloading}
            className="bg-red-600 hover:bg-red-700 h-14"
            data-testid="download-certificate-btn"
          >
            {downloading ? (
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
            ) : (
              <Download className="mr-2" size={20} />
            )}
            Baixar Certificado PDF
          </Button>
          
          <Button
            onClick={handleDownloadBadge}
            variant="outline"
            className="border-yellow-600 text-yellow-500 hover:bg-yellow-600/20 h-14"
            data-testid="download-badge-btn"
          >
            <Award className="mr-2" size={20} />
            Baixar Badge
          </Button>
          
          <Button
            onClick={handleShare}
            variant="outline"
            className="border-zinc-700 text-white hover:bg-zinc-800 h-14"
          >
            <Share2 className="mr-2" size={20} />
            Compartilhar
          </Button>
        </div>

        {/* Validation Info */}
        <div className="mt-8 p-4 bg-zinc-900 rounded-lg border border-zinc-800 text-center">
          <p className="text-gray-400 text-sm">
            Para validar este certificado, acesse:{' '}
            <a 
              href={`/validar/${certificate.certificate_code}`}
              className="text-red-500 hover:text-red-400"
            >
              bimportal.com.br/validar/{certificate.certificate_code}
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default CertificatePage;
